import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/adminService';
import { Image, Plus, Trash2, Upload, Eye, EyeOff, Pencil, X } from 'lucide-react';

const BUCKET = 'customer-banners';

const DEFAULT_SEED_BANNERS = [
  {
    file: 'banner-bg-doorstep.png',
    title: 'Expert Service At Your Doorstep',
    subtitle: 'Flat 10% off on first booking',
    position: 3,
    click_action: 'navigate://Services',
  },
  {
    file: 'banner-bg-roadside.png',
    title: '24/7 Roadside Assistance',
    subtitle: 'Towing · Jump Start · Tyre Change',
    position: 2,
    click_action: 'navigate://Services',
  },
  {
    file: 'banner-bg-ac.png',
    title: 'AC Service & Periodic Care',
    subtitle: 'Book trusted mechanics in 60 seconds',
    position: 1,
    click_action: 'navigate://Services',
  },
];

const uploadPublicBannerFile = async (fileName) => {
  const res = await fetch(`${process.env.PUBLIC_URL || ''}/banners/${fileName}`);
  if (!res.ok) throw new Error(`Missing /banners/${fileName} in web-app/public`);
  const blob = await res.blob();
  const { error } = await supabase.storage.from(BUCKET).upload(fileName, blob, {
    contentType: 'image/png',
    upsert: true,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
  return data.publicUrl;
};

export const seedDefaultCustomerBanners = async () => {
  const rows = [];
  for (const banner of DEFAULT_SEED_BANNERS) {
    const imageUrl = await uploadPublicBannerFile(banner.file);
    rows.push({
      title: banner.title,
      subtitle: banner.subtitle,
      image_url: imageUrl,
      click_action: banner.click_action,
      position: banner.position,
      is_active: true,
      placement: 'home',
    });
  }
  const { error } = await supabase.from('customer_app_banners').insert(rows);
  if (error) throw error;
};

const PLACEMENT_OPTIONS = [
  { value: 'home', label: 'Customer Home' },
  { value: 'rsa_confirm', label: 'RSA Confirm screen' },
  { value: 'rsa_live_status', label: 'RSA Live status' },
];

const ALLOWED_PLACEMENTS = PLACEMENT_OPTIONS.map((o) => o.value);

const placementLabel = (value) =>
  PLACEMENT_OPTIONS.find((o) => o.value === value)?.label || value || 'Customer Home';

const emptyForm = {
  title: '',
  subtitle: '',
  image_url: '',
  click_action: '',
  position: 0,
  is_active: true,
  placement: 'home',
};

const uploadBannerImage = async (file) => {
  const ext = file.name.split('.').pop() || 'jpg';
  const fileName = `banner_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(fileName, file, {
    contentType: file.type || 'image/jpeg',
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
  return data.publicUrl;
};

const CustomerAppBanners = () => {
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formRef = useRef(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['customerAppBanners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_app_banners')
        .select('*')
        .order('position', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('customer_app_banners')
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['customerAppBanners']);
      setFormData(emptyForm);
      setEditingId(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from('customer_app_banners')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['customerAppBanners']);
      setFormData(emptyForm);
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('customer_app_banners').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(['customerAppBanners']),
  });

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, WebP).');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadBannerImage(file);
      setFormData((prev) => ({ ...prev, image_url: url }));
    } catch (err) {
      console.error(err);
      alert('Upload failed. Run supabase/create-customer-app-banners.sql if bucket is missing.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Load 3 default RoadServe banners into customer app?')) return;
    setSeeding(true);
    try {
      await seedDefaultCustomerBanners();
      queryClient.invalidateQueries(['customerAppBanners']);
      alert('Default banners loaded. Refresh customer app to see them.');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to load default banners.');
    } finally {
      setSeeding(false);
    }
  };

  const handlePublish = () => {
    if (!formData.image_url.trim()) {
      alert('Banner image is required (upload or paste URL).');
      return;
    }
    const payload = {
      title: formData.title.trim() || null,
      subtitle: formData.subtitle.trim() || null,
      image_url: formData.image_url.trim(),
      click_action: formData.click_action.trim() || null,
      position: Number(formData.position) || 0,
      is_active: formData.is_active !== false,
      placement: ALLOWED_PLACEMENTS.includes(formData.placement) ? formData.placement : 'home',
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, updates: payload });
      return;
    }
    createMutation.mutate({ ...payload, is_active: true });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      subtitle: item.subtitle || '',
      image_url: item.image_url || '',
      click_action: item.click_action || '',
      position: item.position ?? 0,
      is_active: item.is_active !== false,
      placement: ALLOWED_PLACEMENTS.includes(item.placement) ? item.placement : 'home',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Customer App Banners</h1>
        <p className="text-gray-600 mt-1">
          Manage customer app banners (16:9 recommended). Choose where it shows: Home, RSA Confirm, or Live status.
        </p>
      </div>

      <div ref={formRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {editingId ? <Pencil size={20} /> : <Plus size={20} />}
            {editingId ? 'Edit Banner' : 'Add New Banner'}
          </h2>
          {editingId && (
            <button
              onClick={handleCancelEdit}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <X size={16} /> Cancel
            </button>
          )}
        </div>

        <div
          className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50 cursor-pointer hover:border-primary-400 transition"
          onClick={() => fileInputRef.current?.click()}
        >
          {formData.image_url ? (
            <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover" />
          ) : (
            <div className="h-40 flex flex-col items-center justify-center text-gray-500">
              <Upload size={32} className="mb-2 text-primary-500" />
              <p className="font-medium">{uploading ? 'Uploading…' : 'Click to upload banner image'}</p>
              <p className="text-sm text-gray-400 mt-1">16:9 ratio works best</p>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFilePick} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="url"
            placeholder="Or paste image URL"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 md:col-span-2"
          />
          <input
            type="text"
            placeholder="Title (optional overlay)"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="text"
            placeholder="Subtitle (optional)"
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="text"
            placeholder="Click action e.g. navigate://Services"
            value={formData.click_action}
            onChange={(e) => setFormData({ ...formData, click_action: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="number"
            placeholder="Priority (higher = first)"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Show on</label>
            <select
              value={formData.placement || 'home'}
              onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              {PLACEMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handlePublish}
            disabled={(createMutation.isPending || updateMutation.isPending) || uploading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            <Image size={18} />
            {editingId
              ? (updateMutation.isPending ? 'Saving…' : 'Save Changes')
              : (createMutation.isPending ? 'Publishing…' : 'Publish Banner')}
          </button>
          {editingId && (
            <button
              onClick={handleCancelEdit}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Published Banners</h2>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center space-y-4">
            <p className="text-gray-500">No banners yet.</p>
            <button
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {seeding ? 'Loading…' : 'Load Default Banners (3 images)'}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {items.map((item) => (
              <div key={item.id} className="p-4 flex flex-col sm:flex-row gap-4">
                <img
                  src={item.image_url}
                  alt={item.title || 'Banner'}
                  className="w-full sm:w-48 h-28 object-cover rounded-lg border border-gray-200"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{item.title || '(No title)'}</p>
                  {item.subtitle && <p className="text-sm text-gray-600 mt-1">{item.subtitle}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    {placementLabel(item.placement)} · Priority: {item.position ?? 0} ·{' '}
                    {item.is_active ? 'Active' : 'Hidden'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => handleEdit(item)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                    <button
                      onClick={() => updateMutation.mutate({
                        id: item.id,
                        updates: { is_active: !item.is_active },
                      })}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white flex items-center gap-1 ${
                        item.is_active ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {item.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                      {item.is_active ? 'Hide' : 'Show'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this banner?')) deleteMutation.mutate(item.id);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerAppBanners;
